import { type ChangeEvent, type MouseEvent, useState } from 'react'

import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import * as Icons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { attachmetDateWithTime } from 'helpers/date/attachment-date-with-time'
import { Icons as helpersIcons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { useAddTicket } from 'pages/support/service/react-query/useAddTicket/useAddTicket'

import { CreateTicketModal } from './components/CreateTicketModal'
import {
    StyledAttachmentDeleteButton,
    StyledAttachmentDeleteButtonIcon,
    StyledAttachmentDetails,
    StyledAttachmentListItem,
    StyledAttachmentName,
    StyledAttachmentSize,
    StyledAttachmentType,
    StyledBody,
    StyledBodyBottomButtons,
    StyledBodyBottomLine,
    StyledHeading,
    StyledSelectSubject,
    StyledSelectSubjectIconContainer,
    StyledTextarea
} from './CreateTicket.styled'
import { Attachment } from '../../common/Attachment'

export const CreateTicket = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { mutate } = useAddTicket()

    const [openTicketModal, setOpenTicketModal] = useState(false)
    const [subject, setSubject] = useState<string | null>(null)
    const [text, setText] = useState<string | null>(null)
    const [attachments, setAttachments] = useState<File[]>([])

const handleSetSubject = (subject: string) => {
    setSubject(subject)
    setOpenTicketModal(false)
}

    const handleSetText = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value)
    }

    const handleAttachFile = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files)
            setAttachments(filesArray)
        }
    }

    const handleRemoveFile = (fileToDelete: File) => {
        setAttachments(attachments.filter(file => file !== fileToDelete))
    }

    const handleAddTicket = () => {
        if (subject && text) {
            const newTicket = new FormData()
            newTicket.append('subject', subject)
            newTicket.append('comment_body', text)
            newTicket.append('priority', 'normal')
            attachments?.map(file => newTicket.append(`attachment[]`, file))

            mutate(newTicket, {
                onSuccess: () => {
                    navigate('/support/submit-ticket')
                },
                onError: error => {
                    if (error instanceof AxiosError) {
                        error.response
                            ? addToast(
                                  'error',
                                  error.response.data.error.message
                              )
                            : addToast(
                                  'error',
                                  t('error:errorSomethingWentWrong')
                              )
                    }
                },
                onSettled: () => {
                    queryClient.invalidateQueries('tickets')
                }
            })
        }
    }

    return (
        <div>
            <CreateTicketModal
                isOpen={openTicketModal}
                handleSetSubject={handleSetSubject}
                handleClose={() => setOpenTicketModal(false)}
            />
            <WithHeaderSection
                headerSection={
                    <StyledHeading>{t('createATicket')}</StyledHeading>
                }
            >
                <StyledBody>
                    <StyledSelectSubject
                        onClick={() => setOpenTicketModal(true)}
                    >
                        <StyledSelectSubjectIconContainer>
                            <Icons.chatBubblesWithQuestion />
                        </StyledSelectSubjectIconContainer>
                        {subject ?? `${t('selectAQuestion')} ...`}
                    </StyledSelectSubject>
                    <StyledTextarea
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>): void =>
                            handleSetText(e)
                        }
                    />
                    <ul>
                        {attachments.map(attachment => (
                            <StyledAttachmentListItem key={attachment.name}>
                                <Icons.IconFile />
                                <StyledAttachmentType>
                                    {attachment.type.split('/')[1]}
                                </StyledAttachmentType>
                                <StyledAttachmentDetails>
                                    <StyledAttachmentName>
                                        {attachment.name}{' '}
                                        {attachmetDateWithTime(
                                            attachment.lastModified.toString()
                                        )}
                                    </StyledAttachmentName>
                                    <StyledAttachmentSize>
                                        {(
                                            attachment.size /
                                            (1024 * 1024)
                                        ).toFixed(1)}
                                        mb
                                    </StyledAttachmentSize>
                                </StyledAttachmentDetails>
                                <StyledAttachmentDeleteButton
                                    onClick={() => {
                                        handleRemoveFile(attachment)
                                    }}
                                >
                                    <StyledAttachmentDeleteButtonIcon
                                        src={helpersIcons.close}
                                        alt={t('removeFile')}
                                    />
                                </StyledAttachmentDeleteButton>
                            </StyledAttachmentListItem>
                        ))}
                    </ul>
                    <StyledBodyBottomLine />
                    <StyledBodyBottomButtons>
                        <Attachment
                            handleOnChange={(
                                e: ChangeEvent<HTMLInputElement>
                            ) => handleAttachFile(e)}
                        />
                        <Button
                            text={t('submit')}
                            color='blue'
                            width='10'
                            height='3'
                            font='mont-14-normal'
                            clickFn={() => handleAddTicket()}
                        />
                    </StyledBodyBottomButtons>
                </StyledBody>
            </WithHeaderSection>
        </div>
    )
}
